import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
	fetchLLMUsageQueryOptions,
	fetchPlatformPlansQueryOptions,
	fetchPlatformSubscriptionQueryOptions,
	fetchPlatformUsageQueryOptions,
	fetchTrialStatusQueryOptions,
} from "@/api/platformBilling";
import { BillingContent } from "@/components/BillingContent";
import { PageHeader } from "@/components/PageHeader";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/billing/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Pre-load all billing data to avoid blank screen during navigation
		context.queryClient.ensureQueryData(fetchPlatformPlansQueryOptions());
		context.queryClient.fetchQuery(
			fetchPlatformSubscriptionQueryOptions(userId, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchPlatformUsageQueryOptions(userId, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchTrialStatusQueryOptions(userId, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchLLMUsageQueryOptions(userId, orgId),
		);

		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { userId } = Route.useLoaderData();
	const { orgId } = Route.useParams();
	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgSettings.title")}
				description={t("orgSettings.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>
			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="billing-settings-container"
			>
				<OrganizationSettingsNavigation activeTab="billing" />
				<div className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto">
					<BillingContent userId={userId} orgId={orgId} />
				</div>
			</div>
		</div>
	);
}
